import { FC, ReactNode, SyntheticEvent, useState, useEffect } from 'react';
import { TabItem } from './components.interfaces';
import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  dataTestIdPrefix?: string;
  limitHeight?: boolean;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const {
    children,
    value,
    index,
    dataTestIdPrefix = '',
    limitHeight = false,
    ...other
  } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      data-testid={`${dataTestIdPrefix}-tab-panel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      sx={{
        height: 'calc(100% - var(--tab-group-height))',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        backgroundColor: '#fff',
        borderRadius: '4px',
        overflow: 'auto',
        py: { xs: 1, sm: 2 },
        px: { xs: 1, sm: 3, md: 6 },
      }}
      {...other}
    >
      {value === index ? (
        <Box
          sx={{
            p: 0,
            mt: limitHeight ? 2 : 0,
            height: '100%',
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(8px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '& .MuiGrid-container': {
              width: '100%',
              mx: 'auto',
              px: 0,
            },
            '& .MuiDataGrid-root': {
              border: '1px solid rgba(0,0,0,0.2)',
              backgroundColor: '#fff',
              borderRadius: '4px',
              p: 0,
            },
          }}
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `tab-panel-${index}`,
    'aria-controls': `tab-panel-${index}`,
  };
};

interface TabGroupProps {
  tabs: TabItem[];
  filterSection?: React.ReactNode;
  initiallySelected?: number;
  onTabChange?: (value: number) => void;
  dataTestIdPrefix?: string;
  limitHeight?: boolean;
}

const TabGroup: FC<TabGroupProps> = ({
  tabs,
  initiallySelected = 0,
  onTabChange,
  filterSection,
  dataTestIdPrefix = '',
  limitHeight = false,
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [value, setValue] = useState(initiallySelected);

  useEffect(() => {
    setValue(initiallySelected);
  }, [initiallySelected]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    onTabChange && onTabChange(newValue);
  };

  if (!tabs.length) {
    return null;
  }

  return (
    <Box
      sx={{ width: '100%', height: '100%', pb: { xs: 1, sm: 2 } }}
      data-testid={`${dataTestIdPrefix}-tab-group`}
    >
      <Box
        data-testid={`${dataTestIdPrefix}-tab-group-header`}
        sx={{
          width: '100%',
          display: { xs: 'none', lg: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          position: 'relative',
          zIndex: 2,
          px: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          height: { xs: 'auto', lg: 'var(--tab-group-height)' },
          minHeight: { xs: '48px', lg: 'var(--tab-group-height)' },
          '& .MuiTabs-root': {
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: { xs: '48px', lg: 'var(--tab-group-height)' },
            width: { xs: '100%', lg: 'auto' },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main',
          },
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={translateText('navigation.tabMenu')}
          data-testid={`${dataTestIdPrefix}-tab-group-tabs`}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            flex: { xs: '1 1 100%', lg: '1 1 auto' },
            '& .MuiTabs-flexContainer': {
              height: '100%',
            },
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', lg: 72 },
              fontSize: { xs: '0.75rem', lg: '0.875rem' },
              padding: { xs: '8px 12px', lg: '12px 16px' },
              transition: 'all 0.2s ease',
            },
          }}
        >
          {tabs.map((tab, tabIndex) => (
            <Tab
              key={tab.label}
              data-testid={`${dataTestIdPrefix}-tab-group-tab-${tab.label.replace(' ', '-').toLowerCase()}`}
              label={tab.label}
              {...a11yProps(tabIndex)}
              wrapped={!isMobile}
              onClick={() => {
                tab?.onClickHandler && tab?.onClickHandler(`${tabIndex}`);
              }}
            />
          ))}
        </Tabs>
        {filterSection ? (
          <Box
            data-testid={`${dataTestIdPrefix}-tab-group-filter-section`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: { xs: 0, lg: 4 },
              mt: { xs: 1, lg: 0 },
              borderBottom: { xs: 0, lg: 1 },
              borderColor: 'divider',
              height: { xs: 'auto', lg: '100%' },
              width: { xs: '100%', lg: 'auto' },
            }}
          >
            {filterSection}
          </Box>
        ) : null}
      </Box>
      <Box
        data-testid={`${dataTestIdPrefix}-tab-group-content`}
        sx={{
          mt: { xs: 0, lg: 'var(--tab-group-height)' },
          height: !limitHeight
            ? { xs: '100%', lg: `calc(100% - var(--tab-group-height))` }
            : '100%',
          position: 'relative',
          top: { xs: 0, lg: '-50px' },
          zIndex: 1,
          pb: 2,
        }}
      >
        {tabs.map((tab, tabPanelIndex) => (
          <CustomTabPanel
            key={tab.label + '-panel'}
            value={value}
            index={tabPanelIndex}
            dataTestIdPrefix={dataTestIdPrefix}
            limitHeight={limitHeight}
          >
            {tab.tabPanel}
          </CustomTabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default TabGroup;
